import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Video, Square, RotateCcw, Loader2, Check, Heart, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { uploadDocument, setDocumentSeal } from "../lib/api";
import { SealPicker, sealLabel } from "./ReleaseRules";
import { DevicePicker } from "./record/DevicePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const pickMime = (video) => {
  const c = video ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"] : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  return c.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
};
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const MAX_SECONDS = 15 * 60;

const PROMPTS = [
  "Start with their name — and what you call them when no one's listening.",
  "Tell them about the day they were born, or the day you met.",
  "What's a small, ordinary moment with them you never want to forget?",
  "What did your parents teach you that you hope they carry on?",
  "Describe the kitchen table you grew up around — the sounds, the smells.",
  "What are you proudest of them for? Be specific.",
  "Share a mistake you made and what it taught you.",
  "What do you hope they do when things get hard?",
  "Which family story should never be lost? Tell it now.",
  "What does home mean to you?",
  "Is there anything you've never quite said out loud? Say it here.",
  "Finish with how you'd like them to remember this moment.",
];

const Prompts = ({ active }) => {
  const [i, setI] = useState(0);
  useEffect(() => { if (!active) return; const t = setInterval(() => setI((x) => (x + 1) % PROMPTS.length), 25000); return () => clearInterval(t); }, [active]);
  return (
    <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3 flex items-start gap-3" data-testid="record-prompt">
      <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">If you're stuck, try this</div>
        <p className="text-sm font-serif italic leading-snug mt-0.5" data-testid="record-prompt-text">{PROMPTS[i]}</p>
      </div>
      <button type="button" onClick={() => setI((x) => (x + 1) % PROMPTS.length)} className="text-xs text-primary hover:underline shrink-0" data-testid="record-prompt-next">Another</button>
    </div>
  );
};

export const RecordLetterModal = ({ open, onClose, onUploaded }) => {
  const [mode, setMode] = useState("voice");
  const [to, setTo] = useState("");
  const [seal, setSeal] = useState({ mode: "emergency", until: "" });
  const [showSeal, setShowSeal] = useState(false);
  const [state, setState] = useState("idle"); // idle | live | recording | done | saving
  const [secs, setSecs] = useState(0);
  const [blob, setBlob] = useState(null);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(0);
  const [devices, setDevices] = useState([]);
  const [cam, setCam] = useState("");
  const [mic, setMic] = useState("");
  const previewRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    clearInterval(timerRef.current);
  }, []);

  const attach = (stream) => {
    const el = previewRef.current;
    if (!el) return;
    el.srcObject = stream; el.muted = true;
    el.play().catch(() => {});
  };

  const open_ = useCallback(async (camId, micId) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, ...(micId ? { deviceId: { exact: micId } } : {}) },
      video: mode === "video" ? { width: { ideal: 1280 }, height: { ideal: 720 }, ...(camId ? { deviceId: { exact: camId } } : { facingMode: "user" }) } : false,
    });
    streamRef.current = stream;
    setMic(stream.getAudioTracks()[0]?.getSettings?.().deviceId || micId || "");
    setCam(stream.getVideoTracks()[0]?.getSettings?.().deviceId || camId || "");
    try { setDevices(await navigator.mediaDevices.enumerateDevices()); } catch { /* labels stay generic */ }
    return stream;
  }, [mode]);

  const start = useCallback(async () => {
    setErr(""); setBlob(null); setSecs(0);
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) { setErr("Recording isn't supported in this browser."); return; }
    try {
      await open_(cam, mic);
      setState("live");
    } catch (e) {
      setErr(e?.name === "NotAllowedError" ? "Please allow microphone/camera access to record." : e?.name === "NotFoundError" ? "No camera or microphone was found on this device." : "Couldn't start the microphone or camera.");
    }
  }, [open_, cam, mic]);

  const switchDevice = async (camId, micId) => {
    stopStream();
    try { attach(await open_(camId, micId)); }
    catch { toast.error("Couldn't switch to that device"); try { attach(await open_("", "")); } catch { /* keep the error visible */ } }
  };
  const flipCamera = () => {
    const cams = devices.filter((d) => d.kind === "videoinput");
    const i = cams.findIndex((d) => d.deviceId === cam);
    if (cams.length > 1) switchDevice(cams[(i + 1) % cams.length].deviceId, mic);
  };

  useEffect(() => {
    if (state === "live" && streamRef.current) attach(streamRef.current);
  }, [state]);

  const begin = () => {
    const mime = pickMime(mode === "video");
    const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const b = new Blob(chunksRef.current, { type: rec.mimeType || mime || (mode === "video" ? "video/webm" : "audio/webm") });
      setBlob(b);
      if (previewRef.current) { previewRef.current.srcObject = null; previewRef.current.src = URL.createObjectURL(b); previewRef.current.muted = false; }
      setState("done");
    };
    rec.start(1000);
    recRef.current = rec;
    setState("recording");
    timerRef.current = setInterval(() => setSecs((s) => { if (s + 1 >= MAX_SECONDS) rec.stop(); return s + 1; }), 1000);
  };
  const end = () => { clearInterval(timerRef.current); recRef.current?.stop(); streamRef.current?.getTracks().forEach((t) => t.stop()); };

  const save = async () => {
    if (!blob) return;
    setState("saving"); setProgress(0);
    const ext = blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm";
    const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    const name = `${mode === "video" ? "Video" : "Voice"} letter${to ? ` for ${to.trim()}` : ""} - ${date}.${ext}`;
    try {
      const file = new File([blob], name, { type: blob.type });
      const doc = await uploadDocument(file, "recording", (p) => setProgress(p), "letter,keepsake");
      let sealed = null;
      try { sealed = await setDocumentSeal(doc.id, { ...seal, to_name: to.trim() }); } catch { /* keep the letter even if the rule fails */ }
      toast.success(sealed && sealed.seal.mode !== "open" ? `Letter saved · ${sealed.label}` : "Letter saved to the vault", { description: "Transcript will be ready in a minute." });
      onUploaded?.(sealed ? { ...doc, seal: sealed.seal } : doc);
      reset(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the recording");
      setState("done");
    }
  };

  const reset = useCallback(() => { end(); stopStream(); setState("idle"); setBlob(null); setSecs(0); setErr(""); if (previewRef.current) { previewRef.current.srcObject = null; previewRef.current.removeAttribute("src"); } }, [stopStream]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!open) reset(); }, [open, reset]);
  useEffect(() => () => { clearInterval(timerRef.current); streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="record-letter-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><Heart className="h-5 w-5 text-primary" /> Record a letter</DialogTitle>
          <DialogDescription>A voice or video message for the people you love — kept in the vault, transcribed so it can be found and quoted, and released with your other letters.</DialogDescription>
        </DialogHeader>
        {state === "idle" && (
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-2">
              {[{ id: "voice", Icon: Mic, label: "Voice only" }, { id: "video", Icon: Video, label: "Video" }].map(({ id, Icon, label }) => (
                <button key={id} type="button" onClick={() => setMode(id)} data-testid={`record-mode-${id}`} className={`rounded-xl border px-4 py-3 text-left flex items-center gap-3 transition-colors ${mode === id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                  <Icon className="h-4 w-4 text-primary" /> <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Who is this for? (optional, e.g. Maya and Sam)" data-testid="record-letter-to" />
            <button type="button" onClick={() => setShowSeal((v) => !v)} className="flex items-center gap-2 text-sm text-left rounded-xl border border-border px-3 py-2 hover:border-primary/50" data-testid="record-letter-seal-toggle">
              <Lock className="h-4 w-4 text-primary" /> <span className="flex-1"><span className="font-medium">Release: </span>{sealLabel(seal) || "Open now"}</span><span className="text-xs text-primary">{showSeal ? "Done" : "Change"}</span>
            </button>
            {showSeal && <SealPicker value={seal} onChange={setSeal} />}
            {err && <p className="text-sm text-destructive" data-testid="record-letter-error">{err}</p>}
            <Button onClick={start} className="rounded-full" disabled={(seal.mode === "date" || seal.mode === "both") && !seal.until} data-testid="record-letter-start">{mode === "video" ? <Video className="h-4 w-4" /> : <Mic className="h-4 w-4" />} Get ready</Button>
          </div>
        )}
        {state !== "idle" && (
          <div className="grid gap-3">
            {(state === "live" || state === "recording") && <Prompts active={state === "recording"} />}
            {state === "live" && <DevicePicker devices={devices} video={mode === "video"} cam={cam} mic={mic} onCam={(id) => switchDevice(id, mic)} onMic={(id) => switchDevice(cam, id)} onFlip={flipCamera} />}
            {mode === "video" || state === "done" || state === "saving" ? (
              <video ref={previewRef} playsInline controls={state === "done" || state === "saving"} className={`w-full rounded-xl bg-black ${mode === "video" ? "aspect-video" : "h-14"}`} data-testid="record-letter-preview" />
            ) : (
              <div className="rounded-xl bg-muted/50 h-24 grid place-items-center" data-testid="record-letter-mic">
                <span className={`h-12 w-12 rounded-full grid place-items-center ${state === "recording" ? "bg-destructive/15 text-destructive animate-pulse" : "bg-primary/10 text-primary"}`}><Mic className="h-5 w-5" /></span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="font-mono text-muted-foreground" data-testid="record-letter-timer">{fmt(secs)}{state === "recording" && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-destructive animate-pulse align-middle" />}</span>
              <span className="text-xs text-muted-foreground">{state === "live" ? "Take a breath — press record when you're ready." : state === "recording" ? "Recording… up to 15 minutes." : state === "done" ? "Listen back, then save or record again." : "Saving…"}</span>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              {state === "live" && <Button onClick={begin} className="rounded-full bg-destructive hover:bg-destructive/90" data-testid="record-letter-record"><span className="h-2.5 w-2.5 rounded-full bg-white" /> Record</Button>}
              {state === "recording" && <Button onClick={end} variant="outline" className="rounded-full" data-testid="record-letter-stop"><Square className="h-3.5 w-3.5" /> Stop</Button>}
              {state === "done" && <>
                <Button variant="outline" className="rounded-full" onClick={() => { reset(); start(); }} data-testid="record-letter-retake"><RotateCcw className="h-3.5 w-3.5" /> Record again</Button>
                <Button className="rounded-full" onClick={save} data-testid="record-letter-save"><Check className="h-4 w-4" /> Save to vault</Button>
              </>}
              {state === "saving" && <Button disabled className="rounded-full"><Loader2 className="h-4 w-4 animate-spin" /> {progress > 0 && progress < 1 ? `Uploading ${Math.round(progress * 100)}%` : "Saving…"}</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
